import { FilterType, ModifyType } from "types/enums";
import { z } from "zod";

export const VALUE_DISABLED_OPERATORS = [
    FilterType.IsEmpty,
    FilterType.IsNotEmpty,
    FilterType.IsTrue,
    FilterType.IsFalse,
    FilterType.Always,
];

export const VALUE2_ENABLED_OPERATORS = [FilterType.IsBetween, FilterType.IsBetweenEquals];

export const PATTERN_ENABLED_MODIFICATIONS = [ModifyType.RegexReplace];

export const OUTPUT_DISABLED_MODIFICATIONS = [
    ModifyType.ToBool,
    ModifyType.ToFloat,
    ModifyType.ToInt,
    ModifyType.DateToUnix,
    ModifyType.DoNotSend,
    ModifyType.CzNinToBirthdate,
    ModifyType.CzNinToGender,
    ModifyType.ToBase64,
    ModifyType.FromBase64,
];

const NS = "components.connection-creator.nodes.modify.forms";

/**
 * Field mappings (FE → BE):
 * - Source = filter.source (value_compere_type)
 * - Operator = filter.type (condition_type)
 * - Value = filter.params (condition_value)
 * - Value 2 = filter.params2 (for between/between_inclusive operators)
 * - Modification = change.type (change_type)
 * - Pattern = change.pattern (for regex_replace modification)
 * - Output = change.params (value_to_change)
 */
export const modifyFieldFormSchema = z.object({
    active: z.boolean(),
    title: z.string(),
    subtitle: z.string(),
    data: z.array(
        z.object({
            fieldName: z.string(),
            fields: z.array(
                z
                    .object({
                        source: z.string().min(1, `${NS}.source_required`),
                        operator: z.string().min(1, `${NS}.operator_required`),
                        value: z.string(),
                        value2: z.string(),
                        modification: z.string().min(1, `${NS}.modification_required`),
                        pattern: z.string(),
                        output: z.string(),
                    })
                    .superRefine((val, ctx) => {
                        if (val.operator && !Object.values(FilterType).includes(val.operator as FilterType)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ["operator"],
                                message: `${NS}.operator_invalid`,
                            });
                            return;
                        }

                        if (val.modification && !Object.values(ModifyType).includes(val.modification as ModifyType)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ["modification"],
                                message: `${NS}.modification_invalid`,
                            });
                            return;
                        }

                        if (!VALUE_DISABLED_OPERATORS.includes(val.operator as FilterType) && !val.value) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_small,
                                minimum: 1,
                                type: "string",
                                inclusive: true,
                                path: ["value"],
                                message: `${NS}.value_required`,
                            });
                        }

                        if (VALUE2_ENABLED_OPERATORS.includes(val.operator as FilterType) && !val.value2) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_small,
                                minimum: 1,
                                type: "string",
                                inclusive: true,
                                path: ["value2"],
                                message: `${NS}.value2_required`,
                            });
                        }

                        if (PATTERN_ENABLED_MODIFICATIONS.includes(val.modification as ModifyType) && !val.pattern) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_small,
                                minimum: 1,
                                type: "string",
                                inclusive: true,
                                path: ["pattern"],
                                message: `${NS}.pattern_required`,
                            });
                        }

                        if (!OUTPUT_DISABLED_MODIFICATIONS.includes(val.modification as ModifyType) && !val.output) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_small,
                                minimum: 1,
                                type: "string",
                                inclusive: true,
                                path: ["output"],
                                message: `${NS}.output_required`,
                            });
                        }
                    })
            ),
        })
    ),
});
